import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, TargetIcon, UsersIcon, LayoutIcon, FileTextIcon, CameraIcon, PaletteIcon, TypeIcon, MailIcon, BookOpenIcon, SearchIcon, LinkIcon, MonitorIcon, SmartphoneIcon, ZapIcon, ServerIcon, ShieldCheckIcon, SettingsIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteLatenMakenChecklistPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken checklist | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Complete checklist voor het laten maken van een website. Van domeinnaam en hosting tot content en SEO. Vergeet niets met deze handige checklist van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website laten maken checklist | Noord',
      'og:description': 'Complete checklist voor het laten maken van een website. Van domeinnaam en hosting tot content en SEO.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-laten-maken-checklist',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe lang duurt het om een website te laten maken?',
    a: "Gemiddeld duurt het 2 tot 4 weken om een professionele website te bouwen. Dit hangt af van de complexiteit, het aantal pagina's en hoe snel je feedback en content aanlevert."
  }, {
    q: 'Kan ik zelf de teksten schrijven?',
    a: 'Ja, dat kan zeker. Wij geven je tips en richtlijnen voor effectieve webteksten. Heb je liever dat wij de teksten schrijven? Dan verzorgen wij professionele copywriting die aansluit bij je doelgroep en SEO.'
  }, {
    q: 'Wat moet er op een homepage staan?',
    a: 'Een goede homepage bevat: een duidelijke kop die vertelt wat je doet, je belangrijkste diensten, social proof (reviews of keurmerken), een duidelijke call-to-action en contactmogelijkheden. De homepage is je digitale visitekaartje.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2350">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2351">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2352">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2353" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2354" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2355" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2356">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2357">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2358">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2359">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2360">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2361">/</span>
            <span className="text-accent-light font-medium" data-id="element-2362">
              Website checklist
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2363">
            Checklist voor een succesvolle website
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2364">
            Alles wat je nodig hebt voordat je een website laat maken. Van
            strategie tot livegang — met deze checklist ben je optimaal
            voorbereid.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2365">
            <a href="#/website-laten-maken" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2366">
              Bekijk onze website diensten
              <ArrowRightIcon className="w-5 h-5" data-id="element-2367" />
            </a>
          </div>
        </div>
      </section>

      {/* Stap 1: Strategie */}
      <section className="py-24 bg-surface-muted" data-id="element-2368">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2369">
          <div className="text-center mb-16" data-id="element-2370">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2371">
              Stap 1
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2372">
              Strategie: bepaal de basis
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2373">
              Voordat je begint met bouwen, is het belangrijk om een duidelijke
              strategie te hebben.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2374">
            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2375">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2376">
                <TargetIcon className="w-6 h-6 text-accent" data-id="element-2377" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2378">
                Doel van de website
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2379">
                Wat wil je bereiken? Meer aanvragen, online verkopen of
                naamsbekendheid? Een helder doel bepaalt de richting van je
                website.
              </p>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2380">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2381">
                <UsersIcon className="w-6 h-6 text-accent" data-id="element-2382" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2383">
                Doelgroep bepalen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2384">
                Wie zijn je ideale klanten? Hoe beter je je doelgroep kent, hoe
                effectiever je website wordt ingericht.
              </p>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2385">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2386">
                <LayoutIcon className="w-6 h-6 text-accent" data-id="element-2387" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2388">
                Belangrijkste pagina's
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2389">
                Welke pagina's heeft je website nodig? Denk aan: homepage, over
                ons, diensten, contact en eventueel een blog.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 2: Content */}
      <section className="py-24 bg-white" data-id="element-2390">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2391">
          <div className="text-center mb-16" data-id="element-2392">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2393">
              Stap 2
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2394">
              Content: verzamel je materiaal
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2395">
              Goede content is de basis van een succesvolle website. Bereid dit
              voor.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2396">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2397">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2398">
                <FileTextIcon className="w-6 h-6 text-accent" data-id="element-2399" />
              </div>
              <div data-id="element-2400">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2401">
                  Teksten
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2402">
                  Schrijf heldere teksten die je doelgroep aanspreken. Wij
                  kunnen hierbij helpen met professionele copywriting.
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2403">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2404">
                <CameraIcon className="w-6 h-6 text-accent" data-id="element-2405" />
              </div>
              <div data-id="element-2406">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2407">Foto's</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2408">
                  Professionele foto's maken een groot verschil. Gebruik eigen
                  foto's of kwalitatieve stockfoto's.
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2409">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2410">
                <PaletteIcon className="w-6 h-6 text-accent" data-id="element-2411" />
              </div>
              <div data-id="element-2412">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2413">Logo</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2414">
                  Zorg dat je logo beschikbaar is in hoge kwaliteit (bij
                  voorkeur als vectorbestand).
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2415">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2416">
                <TypeIcon className="w-6 h-6 text-accent" data-id="element-2417" />
              </div>
              <div data-id="element-2418">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2419">
                  Branding
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2420">
                  Heb je een huisstijl? Denk aan kleuren, lettertypen en de
                  uitstraling die je wilt communiceren.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 3: Functionaliteiten */}
      <section className="py-24 bg-surface-muted" data-id="element-2421">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2422">
          <div className="text-center mb-16" data-id="element-2423">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2424">
              Stap 3
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2425">
              Functionaliteiten: wat moet je website kunnen?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2426">
              Bepaal welke functies essentieel zijn voor jouw bedrijf.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2427">
            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2428">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2429">
                <MailIcon className="w-6 h-6 text-accent" data-id="element-2430" />
              </div>
              <div data-id="element-2431">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2432">
                  Contactformulier
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2433">
                  Een goed contactformulier verlaagt de drempel voor potentiële
                  klanten om contact op te nemen.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2434">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2435">
                <BookOpenIcon className="w-6 h-6 text-accent" data-id="element-2436" />
              </div>
              <div data-id="element-2437">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2438">Blog</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2439">
                  Een blog helpt je vindbaarheid in Google en positioneert je
                  als expert in jouw vakgebied.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2440">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2441">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2442" />
              </div>
              <div data-id="element-2443">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2444">SEO</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2445">
                  Zoekmachineoptimalisatie zorgt ervoor dat je website gevonden
                  wordt door je doelgroep.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2446">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2447">
                <LinkIcon className="w-6 h-6 text-accent" data-id="element-2448" />
              </div>
              <div data-id="element-2449">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2450">
                  Integraties
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2451">
                  Denk aan koppelingen met Google Analytics, social media,
                  boekingssystemen of CRM-software.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 4: Design */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2452">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2453" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2454">
          <div className="text-center mb-16" data-id="element-2455">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white font-bold text-sm rounded-full mb-4 border border-white/20" data-id="element-2456">
              Stap 4
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2457">
              Design: hoe ziet je website eruit?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2458">
              Een goed design combineert uitstraling met
              gebruiksvriendelijkheid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2459">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2460">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2461">
                <MonitorIcon className="w-6 h-6 text-white" data-id="element-2462" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2463">
                Gebruiksvriendelijkheid
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2464">
                Bezoekers moeten snel vinden wat ze zoeken. Een logische
                navigatie en duidelijke structuur zijn essentieel.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2465">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2466">
                <SmartphoneIcon className="w-6 h-6 text-white" data-id="element-2467" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2468">
                Responsive design
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2469">
                Je website moet er perfect uitzien op desktop, tablet én mobiel.
                Meer dan 60% van het verkeer komt via mobiel.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2470">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2471">
                <ZapIcon className="w-6 h-6 text-white" data-id="element-2472" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2473">
                Snelle laadtijd
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2474">
                Een snelle website scoort beter in Google en zorgt voor minder
                afhakers. Streef naar een laadtijd onder de 3 seconden.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 5: Livegang */}
      <section className="py-24 bg-white" data-id="element-2475">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2476">
          <div className="text-center mb-16" data-id="element-2477">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2478">
              Stap 5
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2479">
              Livegang: klaar voor lancering
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2480">
              De laatste stappen voordat je website live gaat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2481">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2482">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2483">
                <ServerIcon className="w-6 h-6 text-accent" data-id="element-2484" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2485">Hosting</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2486">
                Kies betrouwbare hosting met goede uptime en snelheid. Wij
                regelen dit volledig voor je.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2487">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2488">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2489" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2490">
                SSL-certificaat
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2491">
                Een SSL-certificaat (https) is verplicht voor veiligheid en
                wordt beloond door Google.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2492">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2493">
                <SettingsIcon className="w-6 h-6 text-accent" data-id="element-2494" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2495">
                SEO basis
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2496">
                Zorg dat de technische SEO op orde is: meta-tags, sitemap,
                robots.txt en snelheidsoptimalisatie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2497">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2498">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2499">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2500">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2501">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2502">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2503">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2504">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2505">
              Kosten website laten maken
            </a>
          </div>
        </div>
      </section>

      {/* Download Checklist */}
      <div className="py-16 bg-surface-muted" data-id="element-2506">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2507">
          <div className="bg-white rounded-2xl shadow-card p-8 lg:p-12 mb-16" data-id="element-2508">
            <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-2509">
              Download de complete checklist (PDF)
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2510">
              Wil je deze checklist altijd bij de hand hebben? Download de
              uitgebreide PDF-versie inclusief handige templates en voorbeelden.
              Van strategie tot livegang, met deze checklist ben je optimaal
              voorbereid.
            </p>
            <a href="#/checklist-pdf" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2511">
              Download PDF
              <ArrowRightIcon className="w-5 h-5" data-id="element-2512" />
            </a>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2513">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2514">
          <div className="text-center mb-16" data-id="element-2515">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2516">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2517">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2518">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2519">
                  <span className="font-bold text-navy-900" data-id="element-2520">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2521" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2522" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2523">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2524" />
    </main>;
}